import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import TrainingPeaksHelp from "@/components/TrainingPeaksHelp";
import TpPremiumUpgrade from "@/components/TpPremiumUpgrade";

export default function TrainingPeaksConnect() {
  const [connected, setConnected] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/integrations/trainingpeaks/events"); setConnected(!!data.connected); }
    catch { setConnected(false); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/trainingpeaks", { url: url.trim() });
      toast.success(`TrainingPeaks connected — ${data.count} workouts`);
      setUrl(""); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not connect that calendar"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.delete("/integrations/trainingpeaks"); await load(); toast.success("TrainingPeaks disconnected"); }
    catch { toast.error("Could not disconnect"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="tp-connect" className="border border-[#4C9AFF]/30 bg-[#4C9AFF]/[0.05] p-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#4C9AFF] mb-2 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> TrainingPeaks</p>
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-[#4C9AFF]" />
      ) : connected ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-[#34C759]">Connected — your sessions sync to the Tracker &amp; journal</p>
          <Button data-testid="tp-connect-disconnect" onClick={disconnect} disabled={busy} variant="outline"
            className="rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase shrink-0">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disconnect"}
          </Button>
        </div>
      ) : (
        <div>
          <TrainingPeaksHelp />
          <div className="flex gap-2">
            <Input data-testid="tp-connect-url" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="webcal://www.trainingpeaks.com/ical/XXXX.ics" className="bg-black border-white/15 text-white text-xs" />
            <Button data-testid="tp-connect-btn" onClick={connect} disabled={busy || !url.trim()}
              className="rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black text-xs uppercase font-semibold shrink-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
            </Button>
          </div>
          <div className="mt-2"><TpPremiumUpgrade /></div>
        </div>
      )}
    </div>
  );
}
